import styled, { keyframes, css } from 'styled-components';
import { cardShadow } from '../../themes/shadows';

// Pulse animation pro live indicator — dot verde que pulsa suavemente
// indicando que a subscription esta ativa (recebendo dados em tempo
// real). Confianca visual: 'sim, esta atualizando'.
const livePulse = keyframes`
  0%, 100% {
    box-shadow: none;
  }
  50% {
    box-shadow: none;
  }
`;

export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

// Header do chart — agora wrap quando o viewport for apertado em vez
// de cortar conteudo no overflow. flex-wrap + row-gap garante quebra
// limpa: titulo+switcher na linha 1, stats/atualizacao/periodos
// fluem pra linha 2 conforme cabem.
export const TagContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin: 12px 20px 8px;
  width: calc(100% - 40px);

  > span {
    margin: 0 auto 0 0;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${(props) => props.theme.colors.label};

    h3 {
      font-weight: 700;
      font-size: 14px;
      color: ${(props) => props.theme.colors.darkerblue};
      margin: 0;
    }
    label {
      color: ${(props) => props.theme.colors.label};
      font-size: 12px;
      font-style: normal;
    }
  }
`;

// Quick switcher: nome da variavel como botao clicavel no header. Abre
// dropdown rapido com a lista de variaveis disponiveis — operador troca
// a curva sem voltar ao Velocimetro pra clicar no gauge.
export const SwitcherButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.darkerblue};
  transition: all 150ms ease;

  :hover {
    background: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 159, 216, 0.08)'
        : 'rgba(59, 191, 241, 0.10)'};
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;

export const SwitcherChevron = styled.span`
  display: inline-block;
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  transition: transform 180ms ease;
  transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
`;

// Dropdown — flutua abaixo do SwitcherButton. Lista clicavel das
// variaveis disponiveis na thing.
export const SwitcherDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 6px;
  box-shadow: none;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const SwitcherOption = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 6px;
  background: ${(props) =>
    props.$selected
      ? props.theme.colors.lightblue
      : 'transparent'};
  color: ${(props) =>
    props.$selected
      ? props.theme.colors.white
      : props.theme.colors.darkerblue};
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${(props) => (props.$selected ? 700 : 500)};
  text-align: left;
  transition: background 120ms ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  :hover {
    background: ${(props) =>
      props.$selected
        ? props.theme.colors.lightblue
        : props.theme.title === 'light'
        ? 'rgba(0, 159, 216, 0.08)'
        : 'rgba(59, 191, 241, 0.10)'};
  }
`;

// Stats inline (Min/Med/Max) no header do chart — da contexto numerico
// do periodo selecionado sem o operador precisar ler a curva inteira.
// Cada item e um par label/value compacto.
export const StatsInline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  margin-right: 4px;
  padding: 4px 12px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0, 0, 0, 0.025)'
      : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${(props) => props.theme.colors.disabled};
`;

export const StatItem = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  line-height: 1;
`;

export const StatLabel = styled.span`
  font-size: 8.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${(props) => props.theme.colors.label};
  opacity: 0.75;
  line-height: 1;
  margin-bottom: 2px;
`;

export const StatValue = styled.span`
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.colors.darkerblue};
  line-height: 1;

  small {
    font-size: 0.7em;
    font-weight: 600;
    color: ${(props) => props.theme.colors.label};
    margin-left: 2px;
  }
`;

// "Ultima atualizacao" agora vive no header, ao lado dos botoes de
// periodo. Antes ficava num FooterWrapper desconectado abaixo do grafico.
export const LastUpdate = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${(props) => props.theme.colors.label};
  margin-right: 8px;

  strong {
    color: ${(props) => props.theme.colors.darkerblue};
    font-weight: 600;
  }
`;

// Dot verde pulsante indicando subscription ativa. Renderizado dentro
// do LastUpdate quando a thingsData ts e recente (<60s). Diz pro
// operador: 'esta vivo, recebendo dados'.
export const LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.green};
  ${css`
    animation: ${livePulse} 2s ease-in-out infinite;
  `}
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  gap: 8px;
  padding: 24px;
  text-align: center;
  color: ${(props) => props.theme.colors.label};

  /* SVG inline herda currentColor do parent (color: label). Antes era
     emoji 📊 com font-size — agora line-icon Lucide bar-chart-2. */
  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
  }

  .message {
    font-size: 14px;
    font-style: italic;
  }
`;

export const ChartArea = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

// Legenda multi-variavel — barra horizontal entre EventTimeline e
// ChartCanvas. So aparece quando ha compareSeries (overlay ativo).
// Cada item: swatch colorido + nome + unidade entre parenteses.
export const LegendBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  padding: 6px 20px 4px;
  width: calc(100% - 40px);
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${(props) => props.theme.colors.darkerblue};
`;

export const LegendSwatch = styled.span`
  width: 14px;
  height: 3px;
  border-radius: 2px;
  flex-shrink: 0;
`;

export const LegendLabel = styled.span`
  font-weight: 700;
`;

export const LegendUnit = styled.span`
  font-size: 10px;
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
`;

// Mini-spinner inline pra legend — aparece quando uma compareSerie
// ainda nao tem dados (fetch em progresso). Visual continuous spin
// usando o @keyframes spin global. 10px diametro pra nao competir
// visualmente com o swatch da serie.
export const LegendSpinner = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.5px solid
    ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.12)'
        : 'rgba(255, 255, 255, 0.12)'};
  border-top-color: ${(props) => props.color || props.theme.colors.lightblue};
  animation: spin 700ms linear infinite;
  vertical-align: middle;
  margin-left: 2px;
`;

// Wrapper do canvas do chart (entre EventTimeline e o footer). Antes
// usava flex:1 + min-height:0 (pra ResponsiveContainer height=100%
// preencher), mas agora a ResponsiveContainer usa aspect={2} —
// determina a propria altura pela largura. Wrapper passa a ser
// content-sized: width: 100% sem flex, deixa o chart "empurrar" a
// altura pra fora pra grid row crescer (minmax(360px, auto) na
// RightColumnGrid permite isso).
export const ChartCanvas = styled.div`
  width: 100%;
  position: relative;
`;

// Badge discreta de "carregando" no canto do chart. Aparece quando
// loadingGraph=true mas NAO bloqueia a renderizacao dos eixos. UX
// pattern: skeleton + indicador pequeno > spinner full-screen. O
// operador ve a estrutura imediatamente (eixos + grid) e o badge
// confirma "to buscando seus dados" sem esconder nada.
const loadingBadgePulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

export const ChartLoadingBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(255, 255, 255, 0.92)'
      : 'rgba(30, 30, 40, 0.92)'};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  color: ${(props) => props.theme.colors.label};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  backdrop-filter: blur(4px);
  pointer-events: none;
  animation: ${loadingBadgePulse} 1.4s ease-in-out infinite;
`;

// Spinner mini do badge — anel girando, sem texto interno.
const loadingSpin = keyframes`
  to { transform: rotate(360deg); }
`;

export const ChartLoadingSpinner = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 1.5px solid ${(props) => props.theme.colors.disabled};
  border-top-color: ${(props) => props.theme.colors.lightblue};
  animation: ${loadingSpin} 0.9s linear infinite;
  flex-shrink: 0;
`;

// Estado de carregamento CENTRALIZADO — substitui o chart enquanto os
// dados sao buscados (ex.: troca de maquina no carrossel). Antes
// mostravamos o esqueleto do chart + badge no canto, mas isso deixava a
// curva ANTIGA visivel durante o fetch (parecia que nao atualizava).
// Agora o chart fica de fora e o spinner + texto ficam no centro.
export const ChartLoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 240px;
  gap: 12px;
  color: ${(props) => props.theme.colors.label};

  .message {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const ChartLoadingSpinnerLarge = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid ${(props) => props.theme.colors.disabled};
  border-top-color: ${(props) => props.theme.colors.lightblue};
  animation: ${loadingSpin} 0.9s linear infinite;
`;

// Footer abaixo do grafico: agrupa Stats (Min/Med/Max) + ultima
// atualizacao. Antes esses dois blocos viviam no header (TagContainer)
// junto com o switcher e o segmented de periodo — header ficava muito
// carregado. Agora o header so tem titulo + controles, e os indicadores
// numericos ficam embaixo, alinhados com o eixo X do chart.
export const ChartFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  width: calc(100% - 40px);
  margin: 4px 20px 14px;
`;

// Botao 'Modo avancado' — line icon (lucide:maximize-2) + texto. Estilo
// ghost igual GhostToggle pra manter consistencia no header, mas com
// cor lightblue (chama atencao como acao, nao toggle de estado).
export const AdvancedToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 12px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 180ms ease;
  border: 1px solid ${(props) => props.theme.colors.lightblue};
  background: transparent;
  color: ${(props) => props.theme.colors.lightblue};

  :hover:not(:disabled) {
    background: ${(props) => props.theme.colors.lightblue};
    color: ${(props) => props.theme.colors.white};
  }

  /* Disabled: dimmed + sem hover. Aplica enquanto data ainda nao
     carregou (loadingGraph) ou periodo sem amostras (data.length===0).
     Evita o operador abrir modal vazio. */
  :disabled {
    opacity: 0.4;
    cursor: not-allowed;
    border-color: ${(props) => props.theme.colors.disabled};
    color: ${(props) => props.theme.colors.label};
  }
`;

// Toggle standalone 'vs anterior' — chip ghost (sem wrapper segmented).
// Economiza espaco no header e diferencia visualmente do segmented de
// periodos (essa toggle e independente).
export const GhostToggle = styled.button`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 180ms ease;
  border: 1px solid
    ${(props) =>
      props.$active ? props.theme.colors.lightblue : props.theme.colors.disabled};
  background: ${(props) =>
    props.$active ? props.theme.colors.lightblue : 'transparent'};
  color: ${(props) =>
    props.$active ? props.theme.colors.white : props.theme.colors.label};

  :hover {
    border-color: ${(props) => props.theme.colors.lightblue};
    color: ${(props) =>
      props.$active ? props.theme.colors.white : props.theme.colors.lightblue};
  }
`;

// Wrapper de segmented control: agrupa os botoes Mes/Semana/Dia/Vs anterior
// num pill unico em vez de tags soltos. Padrao do redesign (igual
// OperationalStatus).
export const PeriodSegmented = styled.div`
  display: inline-flex;
  align-items: stretch;
  height: 28px;
  padding: 3px;
  border-radius: 10px;
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0, 0, 0, 0.035)'
      : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${(props) => props.theme.colors.disabled};
  gap: 2px;
  flex-shrink: 0;
`;

// Botoes Mes/Semana/Dia/Vs anterior: segmented control. Selecionado ganha
// fill + cor branca; nao selecionado e ghost com texto muted.
export const Tag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  cursor: pointer;
  border-radius: 8px;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.4px;
  text-transform: ${(props) => props.transform || 'uppercase'};
  transition: background 180ms ease, color 180ms ease, box-shadow 180ms ease;
  user-select: none;
  white-space: nowrap;

  ${(props) =>
    props.selected
      ? `
        background: ${props.theme.colors.lightblue};
        color: ${props.theme.colors.white};
        box-shadow: none;
      `
      : `
        background: transparent;
        color: ${props.theme.colors.label};
        opacity: 0.7;
      `}

  :hover {
    ${(props) =>
      !props.selected &&
      `color: ${props.theme.colors.lightblue}; opacity: 1;`}
  }
`;

export const Circle = styled.div`
  width: 11px;
  height: 11px;
  margin: 0 5px 0 0;
  border-radius: 50%;
  background: ${(props) => props.color};
`;

export const Label = styled.div`
  font-size: 14px;
  color: ${(props) => props.color};
  margin: 0 10px 0 0;
  line-height: 8px;
  font-weight: bold;
`;

export const FooterWrapper = styled.div`
  display: flex;
  align-items: center;
  width: calc(100% - 120px);
  padding: 0 60px 0 60px;
  margin: 0 0 15px 0;
  h3 {
    color: ${(props) => props.theme.colors.label};
    margin: 0 0 0 auto;
    white-space: nowrap;
    font-size: 12px;
  }
  span {
    color: ${(props) => props.theme.colors.darkblue};
    font-size: 14px;
  }
`;

export const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 15px;
  width: calc(100% - 30px);

  h3 {
    color: ${(props) => props.theme.colors.red};
    font-size: 16px;
    font-weight: 800;
  }

  ::before {
    align-self: center;
    font-weight: 900;
    font-size: 16px;
    content: 'TIME LINE ALARME/ERRO';
  }
`;

export const Timeline = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: 10px;
  box-shadow: ${cardShadow};
  margin: 20px 0;
`;

export const TimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${(props) => props.theme.colors.green};
  position: relative;
  flex: 1;
  padding-bottom: 10px;

  ::before {
    content: '.';
    color: transparent;
    position: absolute;
    bottom: -5px;
    left: 0px;
    border-radius: 50%;
    width: 11px;
    height: 11px;
    background: ${(props) => props.theme.colors.red};
  }
`;

export const Boolline = styled.div`
  display: flex;
  min-height: 100px;
  align-items: flex-end;
`;

export const BoolItem = styled.div`
  width: 50px;
  height: 20px;
  border-radius: 10px;
  background: ${(props) =>
    props.error ? props.theme.colors.red : props.theme.colors.green};
  margin: 0 7px;
`;

export const Col = styled.div`
  display: flex;
  flex-direction: column;
  h3 {
    margin-bottom: 10px;
    text-align: center;
    color: ${(props) => props.theme.colors.label};
  }
`;
